/* eslint-disable max-len */
import React, {
  useState, useRef, useEffect,
} from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { step1Questions, federalFilingStatusQuestions } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function Version2023({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  formatFirstNameMiddleInitial,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_first_name_middle_initial = formatFirstNameMiddleInitial();

  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  // Specific to Federal filing status questions
  const federalFilingStatusForm = useRef();
  const federalFilingStatusInitialQuestions = getInitialQuestions(federalFilingStatusQuestions);
  const [federalFilingStatusQuestionsState, setFederalFilingStatusQuestionsState] = useState(federalFilingStatusInitialQuestions);

  // Specifc to Line 2 Question (Married Filing Jointly with Both Spouses Working)
  const [renderMarriedFilingJointBothSpouseQn, setRenderMarriedFilingJointBothSpouseQn] = useState(data.filing_status === 'married_filing_jointly_or_qualifying_widower' || false);
  const [marriedFilingJointlyBothSpouse, setMarriedFilingJointlyBothSpouse] = useState(data.married_filing_jointly_with_both_spouses_working || false);

  // Withholding Questions - Extra and Reduced Withholdings
  const [extraWithholding, setExtraWithholding] = useState(data.extra_withholding || '');
  const [reducedWithholding, setReducedWithholding] = useState(data.reduced_withholding || '');

  // Exemptions - Default as not_applicable
  const [exemptionsFromWithholding, setExemptionsFromWithholding] = useState(data.exemption || 'not_applicable');

  //  Line 1 Questions (Federal Filing Status)
  const renderFederalFilingStatus = (isReviewStep) => (
    <div>
      <Text bold fontSize="14px" mb="12px">
        Federal filing status (Line 1)
      </Text>
      <FormConsumer
        questions={federalFilingStatusQuestionsState}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          if (question.id === 'filing_status' && value === 'married_filing_jointly_or_qualifying_widower') {
            // Conditionally render Line 2 Question (Married Filing Jointly with Both Spouses Working)
            setRenderMarriedFilingJointBothSpouseQn(true);
          } else {
            setRenderMarriedFilingJointBothSpouseQn(false);
          }
          const questions = federalFilingStatusQuestionsState.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setFederalFilingStatusQuestionsState(questions);
        }}
        ref={federalFilingStatusForm}
      />
    </div>
  );

  // Line 2 Question (Married Filing Jointly with Both Spouses Working)
  const renderWithBothSpouseWorkingQuestion = (isReviewStep) => (
    <div>
      <Text bold fontSize="14px" mb="12px">
        Married Filing Jointly with Both Spouses Working (Line 2)
      </Text>
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          If you are married and you and your spouse are both working and earn similar incomes, select &quot;Yes&quot;. If you and your
          spouse have multiple jobs, and your spouse earns significantly more or less than you, select &quot;No&quot;. Instead, complete
          the Multiple Jobs Worksheet and enter the result on Line 3.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={marriedFilingJointlyBothSpouse}
                checked={marriedFilingJointlyBothSpouse}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setMarriedFilingJointlyBothSpouse(!marriedFilingJointlyBothSpouse);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!marriedFilingJointlyBothSpouse}
                checked={!marriedFilingJointlyBothSpouse}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setMarriedFilingJointlyBothSpouse(!marriedFilingJointlyBothSpouse);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  // Withholding Questions (Line 3 and 4)
  const renderWithholdingQuestions = (isReviewStep) => (
    <>
      <S.QuestionWrapper mb="24px">
        <Flex column>
          <Text bold fontSize="14px" mb="12px">
            Extra Withholding (Line 3)
          </Text>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            width={1}
            mb="7px"
          >
            <S.QuestionLabel isRequired={false}>
              Enter any additional tax you want withheld from each pay period, including any
              amount you want withheld from retirement distributions.
            </S.QuestionLabel>
          </Flex>
        </Flex>
        <Flex position="relative">
          <S.Input
            type="text"
            disabled={isReviewStep}
            value={`$ ${extraWithholding}`}
            width="auto"
            onChange={(e) => {
              const value = e.target.value.replace('$ ', '');
              const re = /^\d*(\.)?\d?\d?$/;
              const isPositiveDecimal = re.test(value);
              if (isPositiveDecimal) {
                setExtraWithholding(value);
              }
            }}
          />
        </Flex>
      </S.QuestionWrapper>
      <S.QuestionWrapper mb="24px">
        <Flex column>
          <Text bold fontSize="14px" mb="12px">
            Reduced Withholding (Line 4)
          </Text>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            width={1}
            mb="7px"
          >
            <S.QuestionLabel isRequired={false}>
              If you expect to report large federal adjustments, federal itemized deductions,
              Montana subtractions, and/or Montana tax credits, you can direct your employer to
              withhold the amount you report on this line. (Caution: Requesting a reduced amount
              of withholding may result in a tax due when you file your tax return.)
            </S.QuestionLabel>
          </Flex>
        </Flex>
        <Flex position="relative">
          <S.Input
            type="text"
            disabled={isReviewStep}
            value={`$ ${reducedWithholding}`}
            width="auto"
            onChange={(e) => {
              const value = e.target.value.replace('$ ', '');
              const re = /^\d*(\.)?\d?\d?$/;
              const isPositiveDecimal = re.test(value);
              if (isPositiveDecimal) {
                setReducedWithholding(value);
              }
            }}
          />
        </Flex>
      </S.QuestionWrapper>
    </>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <Flex column mb="20px">
      <Text bold fontSize="14px" mb="12px">
        Exemptions from withholding (Line 5)
      </Text>
      <S.QuestionWrapper>
        <Flex mb="7px">
          <S.QuestionLabel>
            You may be entitled to claim an exemption from Montana income tax withholding.
            If applicable, mark one box below to indicate the reason why you believe you are exempt.
          </S.QuestionLabel>
        </Flex>
        <label>
          <Flex mb="8px">
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="member_of_registered_tribe"
              checked={exemptionsFromWithholding === 'member_of_registered_tribe'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            I am an enrolled member of a registered tribe, I live on the reservation
            of that tribe and I earn wages from work performed on that reservation.
          </Flex>
        </label>
        <label>
          <Flex mb="8px">
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="member_of_reserve_national_guard"
              checked={exemptionsFromWithholding === 'member_of_reserve_national_guard'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            I am a member of the Reserve or National Guard, and my compensation
            is earned under U.S.C. Title 10.
          </Flex>
        </label>
        <label>
          <Flex mb="8px">
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="resident_north_dakota"
              checked={exemptionsFromWithholding === 'resident_north_dakota'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            I am a resident of North Dakota.
          </Flex>
        </label>
        <label>
          <Flex mb="8px">
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="spouse_military"
              checked={exemptionsFromWithholding === 'spouse_military'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            I am a resident of another state living in Montana solely to be with my spouse,
            who is a resident of the same state and a member of the U.S. armed forces
            assigned to a military location in Montana.
          </Flex>
        </label>
        <label>
          <Flex>
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="not_applicable"
              checked={exemptionsFromWithholding === 'not_applicable'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            Not applicable.
          </Flex>
        </label>
      </S.QuestionWrapper>
    </Flex>
  );

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
    </div>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {renderFederalFilingStatus()}
          {renderMarriedFilingJointBothSpouseQn && renderWithBothSpouseWorkingQuestion()}
          {renderWithholdingQuestions()}
          {renderExemptionsFromWithholding()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;

            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }

            // Check for the required fields within federal filing status questions
            if (federalFilingStatusForm && federalFilingStatusForm.current) {
              federalFilingStatusForm.current.validate((error) => {
                if (error) err = error;
              });
            }

            if (err) {
              return;
            }
            // No error, proceed to save
            // - save answers to server
            // - processed to next step

            const federalFilingStatusOption = federalFilingStatusQuestionsState[0].answer.answer.filing_status;
            const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            const exemptionData = { exemption: exemptionsFromWithholding };
            const filingStatusData = { filing_status: federalFilingStatusOption };
            const withholdingData = { extra_withholding: extraWithholding, reduced_withholding: reducedWithholding };
            const marriedFilingJointlyBothSpouseData = { married_filing_jointly_with_both_spouses_working: marriedFilingJointlyBothSpouse };

            const data = {
              ...filingStatusData,
              ...stepOneData,
              ...exemptionData,
              ...withholdingData,
              ...marriedFilingJointlyBothSpouseData,
            };

            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {renderFederalFilingStatus(true)}
        {renderMarriedFilingJointBothSpouseQn && renderWithBothSpouseWorkingQuestion(true)}
        {renderWithholdingQuestions(true)}
        {renderExemptionsFromWithholding(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default Version2023;
